import React from 'react';
import {
  BrowserRouter, Navigate, Routes, Route,
} from 'react-router-dom';
import { NotificationsProvider, UIConfigurator } from '@doveit/bricks';
import { SWRConfig } from 'swr';
import { Auth0Provider } from '@auth0/auth0-react';
import ScrollToTop from './components/scroll-to-top/ScrollToTop';
import AppLayout from './layouts/app-layout/AppLayout';
import AgentsSearchPage from './agent/pages/search-by-geo/AgentsSearchPage';
import CallCenterContactProcessPage from './call-center/pages/process/CallCenterContactProcessPage';
import CreateLeadPage from './lead/pages/create/CreateLeadPage';
import SPBPage from './pages/spb/SPBPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import CreateIntentPage from './intent/pages/create/CreateIntentPage';
import ViewProspectPage from './prospect/pages/view/ViewProspectPage';
import ViewIntentPage from './intent/pages/view/ViewIntentPage';
import AssignmentModePage from './prospect/pages/assignment-mode/AssignmentModePage';
import ViewContactPage from './contact/pages/view/ViewContactPage';
import SearchContactPage from './contact/pages/search/SearchContactPage';
import ViewSearchCriteriaPage from './search-criteria/pages/view/ViewSearchCriteriaPage';
import CreateSearchCriteriaPage from './search-criteria/pages/create/CreateSearchCriteriaPage';
import LoadGoogleMapsAPI from './components/load-google-maps-api/LoadGoogleMapsAPI';
import CreateRumorPage from './rumor/pages/create/CreateRumorPage';
import ViewJobApplicationPage from './job-application/pages/view/ViewJobApplicationPage';
import JobApplicationSerpPage from './job-application/pages/serp/JobApplicationSerpPage';
import useRBAC from './hooks/use-rbac/useRBAC';
import { UserRole } from './types';
import ViewApplicationNotificationsPage from './application-notification/pages/view/ViewApplicationNotificationsPage';
import CreateTrackedLinksPage from './marketing/pages/tracked-links/CreateTrackedLinksPage';
import CataloguePage from './marketing/pages/catalogue/CataloguePage';
import { ApplicationNotificationsProvider } from './application-notification/containers/application-notifications-provider/ApplicationNotificationsProvider';
import ViewResourcesPage from './resources/pages/view/ViewResourcesPage';
import { swrOnErrorRetry } from './config/swr-config';
import { useCurrentAgentSync } from './hooks/use-agent/useAgent';
import { DOVE_LOGO_ASCII, NotFoundPage } from './constants';
import RequireAuth from './containers/require-auth/RequireAuth';
import CallCenterMessageCenterPage from './call-center/pages/message-center/CallCenterMessageCenterPage';
import ManageAssignmentPage from './prospect/pages/manage-assignment/ManageAssignmentPage';
import ViewAgentPage from './agent/pages/view/ViewAgentPage';
import ViewAgencyPage from './agency/pages/view/ViewAgencyPage';
import OfferModePage from './offer/pages/offer-mode/OfferModePage';
import ManageOfferPage from './offer/pages/manage-offer/ManageOfferPage';
import {
  CallCenterContactDetailPage, CallCenterContactProcessColdLeadsDetailPage, CallCenterContactProcessLeadsDetailPage, CallCenterContactProcessSurveysDetailPage,
} from './call-center/pages/view/CallCenterView';
import ViewPropertyMatchingPage from './property/pages/matching/ViewPropertyMatchingPage';
import ViewPropertyPage from './property/pages/view/ViewPropertyPage';
import IntentSerpPage from './intent/pages/serp/IntentSerpPage';
import ProspectSerpPage from './prospect/pages/serp/ProspectSerpPage';
import { AUTH0_CLIENT_DEFAULT_OPTIONS } from './providers/clients/auth0Client';
import GenerateShowcasePage from './marketing/pages/generate-showcase/GenerateShowcasePage';
import ViewProspectMatchingPage from './prospect/pages/matching/ViewProspectMatchingPage';
import ViewRumorPage from './rumor/pages/view/ViewRumorPage';
import GenerateAgentBrochurePage from './marketing/pages/generate-agent-brochure/GenerateAgentBrochurePage';
import GenerateMaterialsPage from './marketing/pages/generate-materials/GenerateMaterialsPage';
import BrandTemplateListPage from './marketing/pages/brand-template-list/BrandTemplateListPage';
import DesignListPage from './marketing/pages/design-list/DesignListPage';
import ViewRumorMatchingPage from './rumor/pages/matching/ViewRumorMatchingPage';
import RumorSerpPage from './rumor/pages/serp/RumorSerpPage';
import PropertySerpPage from './property/pages/serp/PropertySerpPage';
import ViewTaskPage from './task/pages/ViewTaskPage';
import TaskSerpPage from './task/pages/serp/TaskSerpPage';
import AgentSerpPage from './agent/pages/serp/AgentSerpPage';
import { JobTitle } from './domain/types';
import CreateAgencyPage from './agency/pages/create/CreateAgencyPage';
import ViewInvoicePage from './invoice/pages/view/ViewInvoicePage';
import InvoiceSerpPage from './invoice/pages/serp/InvoiceSerpPage';
import CreateJobApplicationWithApplicantPage from './job-application/pages/create/CreateJobApplicationWithApplicantPage';
import LoginCanvaPage from './marketing/pages/login-canva/LoginCanvaPage';
import UseBrandTemplatePage from './marketing/pages/use-brand-template/UseBrandTemplatePage';
import CanvaAuthContextProvider from './contexts/canva-auth-context/CanvaAuthContext';
import RequireCanvaAuth from './containers/require-cava-auth/RequireCanvaAuth';
import MarketingDesignExportJobContextProvider from './contexts/marketing-design-export-job-context/MarketingDesignExportJobContext';
import ExploreCanvaResourcesPage from './marketing/pages/explore-canva-resources/ExploreCanvaResourcesPage';
import { UserPermissionsProvider } from './contexts/user-permissions-context/UserPermissionsContext';
import useUserPermissions from './hooks/use-user-permissions/useUserPermissions';
import { GlobalStyle } from './theme/styles/GlobalStyles';

const AppRoutes: React.FC = () => {
  const { userHasAnyRole, mainUserRole, userIsAreaManager } = useRBAC();
  const { data: currentAgent } = useCurrentAgentSync();
  const userPermissions = useUserPermissions(['contacts', 'rumors', 'jobApplications']);

  const userIsBroker = React.useMemo(
    () => currentAgent?.jobTitle === JobTitle.BROKER,
    [currentAgent],
  );

  const isContentWriter = React.useMemo(
    () => mainUserRole === UserRole.CONTENT_WRITER,
    [mainUserRole],
  );

  const isCanvaAccessEnabled = React.useMemo(
    () => userHasAnyRole([UserRole.ADMIN, UserRole.MARKETING]) || currentAgent?.configuration?.marketing?.isCanvaAccessEnabled,
    [currentAgent?.configuration?.marketing?.isCanvaAccessEnabled, userHasAnyRole],
  );

  const userIsActiveAreaManagerOrActiveBroker = React.useMemo(
    () => (userIsAreaManager || userIsBroker) && (currentAgent && !currentAgent.contract.endDate),
    [userIsAreaManager, userIsBroker, currentAgent],
  );

  return (
    <Routes>
      {/** SPB */}
      <Route
        path="/spb"
        element={(
          <RequireAuth>
            <SPBPage />
          </RequireAuth>
        )}
      />

      {/** Dashboard */}
      <Route
        index
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <DashboardPage />
          </RequireAuth>
        )}
      />

      {/** Call-center - Leads */}
      <Route
        path="/call-center/contacts/process"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CallCenterContactProcessPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/call-center/contacts/create"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CreateLeadPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/call-center/contacts/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN, UserRole.CONTENT_EDITOR])}>
            <CallCenterContactDetailPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/call-center/contacts/process/:id/leads"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CallCenterContactProcessLeadsDetailPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/call-center/contacts/process/:id/cold-leads"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CallCenterContactProcessColdLeadsDetailPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/call-center/contacts/process/:id/surveys"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CallCenterContactProcessSurveysDetailPage />
          </RequireAuth>
        )}
      />

      {/** Call center - Message center */}
      <Route
        path="/call-center/messaging"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN])}>
            <CallCenterMessageCenterPage />
          </RequireAuth>
        )}
      />

      {/** Contacts */}
      <Route
        path="/contacts/search"
        element={(
          <RequireAuth renderConditions={!!userPermissions.contacts.canSearch}>
            <SearchContactPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/contacts/:id"
        element={(
          <RequireAuth renderConditions={!!userPermissions.contacts.canSee}>
            <ViewContactPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/contacts/:id/intents/:intentId"
        element={<Navigate to="/intents/:intentId" />}
      />

      {/** Intents */}
      <Route
        path="/intents/create"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <CreateIntentPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/intents/explore"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <IntentSerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/intents/:id"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <ViewIntentPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/intents/:id/offer"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <OfferModePage />
          </RequireAuth>
        )}
      />
      <Route
        path="/intents/:id/offer/:mode"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <ManageOfferPage />
          </RequireAuth>
        )}
      />

      {/** Search criteria */}
      <Route
        path="/search-criteria/create"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CONTENT_EDITOR, UserRole.CALL_CENTER, UserRole.ADMIN, UserRole.AGENT])}>
            <CreateSearchCriteriaPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/search-criteria/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CONTENT_EDITOR, UserRole.CALL_CENTER, UserRole.ADMIN, UserRole.AGENT])}>
            <ViewSearchCriteriaPage />
          </RequireAuth>
        )}
      />

      {/** Rumors */}
      <Route
        path="/rumors/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT])}>
            <RumorSerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/rumors/create"
        element={(
          <RequireAuth renderConditions={!!userPermissions.rumors.canAdd}>
            <CreateRumorPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/rumors/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CALL_CENTER, UserRole.ADMIN, UserRole.AGENT])}>
            <ViewRumorPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/rumors/:id/matching"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT, UserRole.COORDINATOR])}>
            <ViewRumorMatchingPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/prospects/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT])}>
            <ProspectSerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/prospects/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AREA_MANAGER, UserRole.AGENT, UserRole.CALL_CENTER, UserRole.CONTENT_EDITOR, UserRole.COORDINATOR, UserRole.FINANCE])}>
            <ViewProspectPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/prospects/:id/matching"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT, UserRole.COORDINATOR])}>
            <ViewProspectMatchingPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/prospects/:id/assignment/:action"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <AssignmentModePage />
          </RequireAuth>
        )}
      />
      <Route
        path="/prospects/:id/assignment/:action/:mode"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <ManageAssignmentPage />
          </RequireAuth>
        )}
      />

      {/** Properties */}
      <Route
        path="/properties/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT, UserRole.CALL_CENTER, UserRole.CONTENT_EDITOR, UserRole.FINANCE])}>
            <PropertySerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/properties/:id"
        element={(
          <RequireAuth>
            <ViewPropertyPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/properties/:id/matching"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT, UserRole.COORDINATOR])}>
            <ViewPropertyMatchingPage />
          </RequireAuth>
        )}
      />

      {/** Agents */}
      <Route
        path="/agents/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.CALL_CENTER, UserRole.CONTENT_EDITOR, UserRole.HR, UserRole.FINANCE, UserRole.MARKETING]) || userIsActiveAreaManagerOrActiveBroker}>
            <AgentSerpPage />
          </RequireAuth>
        )}
      />

      <Route
        path="/agents/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.HR, UserRole.FINANCE, UserRole.CONTENT_EDITOR, UserRole.CALL_CENTER])}>
            <ViewAgentPage />
          </RequireAuth>
        )}
      />

      {/** Agents/Geo */}
      <Route
        path="/agent/search/*"
        element={(
          <RequireAuth renderConditions={!isContentWriter}>
            <AgentsSearchPage />
          </RequireAuth>
        )}
      />

      {/** Agencies */}
      <Route
        path="/agencies/create"
        element={(
          <RequireAuth renderConditions={userHasAnyRole(UserRole.ADMIN)}>
            <CreateAgencyPage />
          </RequireAuth>
        )}
      />

      {/** Marketing */}
      <Route
        path="/marketing"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.AGENT, UserRole.ADMIN, UserRole.MARKETING])}>
            <CreateTrackedLinksPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/marketing/catalogues"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.AGENT, UserRole.ADMIN, UserRole.MARKETING])}>
            <CataloguePage />
          </RequireAuth>
        )}
      />
      <Route
        path="/marketing/generate"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.AGENT, UserRole.ADMIN, UserRole.CONTENT_EDITOR, UserRole.COORDINATOR, UserRole.MARKETING])}>
            <GenerateMaterialsPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/marketing/generate/showcase"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.AGENT, UserRole.ADMIN, UserRole.CONTENT_EDITOR, UserRole.COORDINATOR, UserRole.MARKETING])}>
            <GenerateShowcasePage />
          </RequireAuth>
        )}
      />
      <Route
        path="/marketing/generate/agent-brochure"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.AGENT, UserRole.ADMIN, UserRole.CONTENT_EDITOR, UserRole.COORDINATOR, UserRole.MARKETING])}>
            <GenerateAgentBrochurePage />
          </RequireAuth>
        )}
      />

      <Route element={<CanvaAuthContextProvider />}>
        <Route
          path="/marketing/canva/explore"
          element={(
            <RequireAuth renderConditions={isCanvaAccessEnabled}>
              <RequireCanvaAuth>
                <ExploreCanvaResourcesPage />
              </RequireCanvaAuth>
            </RequireAuth>
          )}
        />
        <Route
          path="/marketing/canva/brand-templates/:id/fill"
          element={(
            <RequireAuth renderConditions={isCanvaAccessEnabled}>
              <RequireCanvaAuth>
                <UseBrandTemplatePage />
              </RequireCanvaAuth>
            </RequireAuth>
          )}
        />
        <Route
          path="/marketing/canva/brand-templates"
          element={(
            <RequireAuth renderConditions={isCanvaAccessEnabled}>
              <RequireCanvaAuth>
                <BrandTemplateListPage />
              </RequireCanvaAuth>
            </RequireAuth>
          )}
        />
        <Route
          path="/marketing/canva/designs"
          element={(
            <RequireAuth renderConditions={isCanvaAccessEnabled}>
              <RequireCanvaAuth>
                <MarketingDesignExportJobContextProvider>
                  <DesignListPage />
                </MarketingDesignExportJobContextProvider>
              </RequireCanvaAuth>
            </RequireAuth>
          )}
        />
        <Route
          path="/marketing/canva/login"
          element={(
            <RequireAuth renderConditions={isCanvaAccessEnabled}>
              <LoginCanvaPage />
            </RequireAuth>
          )}
        />
      </Route>

      {/** Admin - Agency */}
      <Route
        path="/agencies/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.HR, UserRole.FINANCE, UserRole.CONTENT_EDITOR, UserRole.CALL_CENTER])}>
            <ViewAgencyPage />
          </RequireAuth>
        )}
      />

      {/** Invoices */}
      <Route
        path="/invoices/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.FINANCE])}>
            <InvoiceSerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/invoices/:id"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.FINANCE, UserRole.AGENT])}>
            <ViewInvoicePage />
          </RequireAuth>
        )}
      />

      {/** Job Applications */}
      <Route
        path="/job-applications/explore"
        element={(
          <RequireAuth renderConditions={userPermissions.jobApplications.canExploreAll}>
            <JobApplicationSerpPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/job-applications/create"
        element={(
          <RequireAuth renderConditions={userPermissions.jobApplications.canAdd}>
            <CreateJobApplicationWithApplicantPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/job-applications/:id"
        element={(
          <RequireAuth renderConditions={userPermissions.jobApplications.canSee}>
            <ViewJobApplicationPage />
          </RequireAuth>
        )}
      />

      {/** Resources */}
      <Route
        path="/resources/:agencyId?/:section"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.AGENT, UserRole.CONTENT_EDITOR, UserRole.HR])}>
            <ViewResourcesPage />
          </RequireAuth>
        )}
      />

      {/** Notifications */}
      <Route
        path="/notifications"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.CONTENT_EDITOR, UserRole.AGENT])}>
            <ViewApplicationNotificationsPage />
          </RequireAuth>
        )}
      />

      {/** Callback */}
      <Route
        path="/callback"
        element={null}
      />

      {/** Tasks */}
      <Route
        path="/tasks/explore"
        element={(
          <RequireAuth renderConditions={userHasAnyRole([UserRole.ADMIN, UserRole.CONTENT_EDITOR])}>
            <TaskSerpPage />
          </RequireAuth>
        )}
      />

      <Route
        path="/tasks/:id"
        element={(
          <RequireAuth>
            <ViewTaskPage />
          </RequireAuth>
        )}
      />

      {/** No match */}
      <Route
        path="*"
        element={<NotFoundPage />}
      />
    </Routes>
  );
};

const App: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(`%c${DOVE_LOGO_ASCII}`, 'color: #E31D93;');
  }, []);
  const isLocalhost = window.location.hostname === 'localhost';

  return (
    <UIConfigurator>
      <GlobalStyle />
      <Auth0Provider
        domain={AUTH0_CLIENT_DEFAULT_OPTIONS.domain}
        clientId={AUTH0_CLIENT_DEFAULT_OPTIONS.clientId}
        authorizationParams={AUTH0_CLIENT_DEFAULT_OPTIONS.authorizationParams}
        onRedirectCallback={(appState) => {
          window.location.replace(appState?.returnTo || window.location.href);
        }}
        cacheLocation={isLocalhost ? 'localstorage' : undefined}
        useRefreshTokens={isLocalhost ? true : undefined}
      >
        <SWRConfig
          value={{
            revalidateOnFocus: false,
            revalidateIfStale: false,
            onErrorRetry: swrOnErrorRetry,
          }}
        >
          <UserPermissionsProvider>
            <BrowserRouter>
              <ScrollToTop>
                <NotificationsProvider>
                  <ApplicationNotificationsProvider>
                    <AppLayout>
                      <LoadGoogleMapsAPI>
                        <AppRoutes />
                      </LoadGoogleMapsAPI>
                    </AppLayout>
                  </ApplicationNotificationsProvider>
                </NotificationsProvider>
              </ScrollToTop>
            </BrowserRouter>
          </UserPermissionsProvider>
        </SWRConfig>
      </Auth0Provider>
    </UIConfigurator>
  );
};

export default App;
